/**
 * scaffoldCover.ts
 *
 * The decision behind the preview's scaffolding cover ("Scaffolding" /
 * "Building your page"), extracted pure so it can be pinned by tests.
 *
 * WHAT THE COVER HIDES. A from-scratch project boots its machine on the starter
 * scaffold. For the landing tier that is landing-nextjs-starter, whose home page
 * is a complete pre-built template with placeholder copy ("Heading one",
 * "Book a Call"; the live list is server/config/starter-fingerprints.json,
 * checked against the starter in every image build). The dev server serves it the instant the
 * machine is up, and the preview pane is a live view of that dev server. The
 * agent's first turn exists to rewrite it. Until a turn has COMPLETED, the
 * template is not the customer's page and must not be presented as one.
 *
 * THE RULE (product decision, 2026-09-07): a scratch project's first task holds
 * the cover until a turn on it COMPLETES. There is no earlier drop — not on the
 * first file write, not on a snapshot, not on a second message — and a turn
 * that the owner STOPS, or that FAILS, does not drop it either: it ended with
 * the machine still serving the starter (Aima, 2026-09-07: "when I stop the
 * agent midway I still see the base template"). The cover then says so and the
 * next message picks the build up. Every OTHER moment (follow-up tasks,
 * imports, a template that has booted) shows the real, working app, because
 * covering a working app makes it unusable while the agent works.
 *
 * Why only DURABLE facts (2026-09-07, Talha's eyelash-business build: the
 * template was on screen while the agent was still asking its first question).
 * The previous rule also read three session heuristics, and each could go false
 * mid-first-build and drop the cover onto the untouched template:
 *   - a per-session "has rendered before" latch, flipped by any /dev-server
 *     poll that returned a snapshotUrl. The server returns one for any
 *     non-running status once project.snapshotAt exists, and snapshotAt is
 *     stamped ~5s after the first boot — a snapshot OF the template — so one
 *     health-check flap after that latched the cover off for the session;
 *   - a "one command message so far" count, which a second message typed
 *     mid-build breaks;
 *   - task.filesChanged, which the server never populates.
 * A fact that can change mid-turn without the turn ending cannot gate a cover
 * whose contract is "until a turn completes".
 *
 * Why `hasCompletedTurn` and not `completedAt`: Task.completeExecution stamps
 * completedAt on FAILURE too, so it cannot tell "the page exists" from "the
 * build died on the starter". hasCompletedTurn is computed by the server on
 * GET /tasks/:id from the task's command logs (CommandLog.appExistsFilter:
 * a completed non-plan turn that changed files, OR an interrupted turn the
 * server's presentability check saw the owner's app on — out of credit or
 * stopped after a working app was written, 2026-10-03, task 6ac0bf5c; see
 * utils/interruptedBuild.ts) — every turn writes one, so it survives reloads
 * and needs no backfill.
 *
 * Why the latch (`completedOnce`) exists — the 2026-08-26 stuck-cover bug
 * (staging, Billwise): after the first turn completes, the landing/fullstack
 * build gates may dispatch a SILENT repair turn — deliberately no TaskChat
 * bubble (worker.js, "Silent fix-turn dispatch"). That turn flips the task
 * back to 'in_progress' while the chat already shows the final summary.
 * Scaffolding is a ONE-TIME event. Once a turn on this task has completed, the
 * app exists and the cover must never return, whatever status does next.
 */

const TERMINAL_TASK_STATUSES = new Set(['completed', 'failed', 'cancelled']);

/** True for the three statuses after which a turn is over. */
export function isTerminalTaskStatus(status: unknown): boolean {
  return typeof status === 'string' && TERMINAL_TASK_STATUSES.has(status);
}

export interface TaskCompletionInputs {
  /** task.status as the client currently knows it */
  status?: string | null;
  /** the session's ExecutionStatus, which has its own update paths */
  executionStatus?: string | null;
}

/**
 * True the moment there is ANY evidence a turn on this task has COMPLETED.
 * 'failed' and 'cancelled' are terminal but are NOT completion: the turn ended
 * with the starter still on the machine.
 */
export function taskHasCompletedOnce(input: TaskCompletionInputs): boolean {
  return input.status === 'completed' || input.executionStatus === 'completed';
}

export interface FirstTaskInputs {
  /** How many tasks the client knows of on this project (including this one). */
  siblingTaskCount: number;
  /** project.firstBootCompletedAt — the machine has served at least once. */
  projectFirstBootCompletedAt?: string | Date | null;
  /** task.createdAt */
  taskCreatedAt?: string | Date | null;
}

function toMs(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Is this the project's FIRST task — the one whose first turn builds the app?
 *
 * Two facts, either of which says "not first":
 *   1. The task list already holds another task for this project. The list is
 *      project-scoped, but it arrives one round trip after the page mounts, so
 *      on a cold load it can be empty for a moment.
 *   2. Durable, needs no fetch: the machine had ALREADY served (project.
 *      firstBootCompletedAt) before this task was created. A first task is
 *      created together with the project, minutes before its machine boots;
 *      any later task is created after. (firstBootCompletedAt resets when a
 *      machine is recreated — that only degrades this back to fact 1.)
 */
export function taskIsFirstOnProject(i: FirstTaskInputs): boolean {
  if (i.siblingTaskCount > 1) return false;
  const firstBoot = toMs(i.projectFirstBootCompletedAt);
  const created = toMs(i.taskCreatedAt);
  if (firstBoot != null && created != null && firstBoot < created) return false;
  return true;
}

export interface ScaffoldCoverInputs {
  /** project.source — the cover is for from-scratch builds only */
  source?: string | null;
  /** first /dev-server poll has answered (don't decide from a blank slate) */
  devServerChecked: boolean;
  /** output of taskIsFirstOnProject — a follow-up task never scaffolds */
  isFirstTaskOnProject: boolean;
  /**
   * task.hasCompletedTurn — server-computed on GET /tasks/:id: the app exists
   * (a turn completed with files, or the presentability check saw it after an
   * interrupted turn). The reload-surviving form of "the page exists".
   */
  hasCompletedTurn?: boolean | null;
  /** task.status as the client currently knows it */
  status?: string | null;
  /** sticky output of taskHasCompletedOnce — once true, stays true for the task */
  completedOnce: boolean;
  /** project.templateId — set only for a gallery import */
  templateId?: string | null;
  /** project.firstBootCompletedAt — the machine has served at least once */
  firstBootCompletedAt?: string | Date | null;
  /**
   * A plan is saved and nothing has been built from it yet. The preview in that
   * state is the UNTOUCHED starter — developer scaffolding ("Edit
   * client/src/pages/HomePage.tsx", localhost ports, "Reference CRUD vertical")
   * that a non-technical owner should never be shown (Aima, 2026-09-21).
   *
   * `hasCompletedTurn` no longer goes true off a plan turn: CommandLog now
   * carries a `mode` field, and both hasCompletedTurn (routes/tasks.js) and the
   * thumbnail starter-skip (thumbnailService) exclude `mode: 'plan'` rows
   * (2026-09-21). This clause still earns its place for two OTHER reasons that
   * fix doesn't touch:
   *   - a TEMPLATE project trips the templateId+firstBootCompletedAt early
   *     return below and would show the raw starter during the plan states;
   *   - the client latch `completedOnce` (taskHasCompletedOnce) is fed by
   *     task.status, and the worker still publishes status 'completed' for a
   *     clarify-only plan turn (worker.js ~7090) — that's task status, not
   *     CommandLog, and is deliberately left unchanged here (its own
   *     follow-up).
   * So this stays its own clause rather than folding into either.
   */
  planAwaitingBuild?: boolean;
  /**
   * The FIRST build is running on a project that has never been built, and a
   * plan turn preceded it. Needs its own clause for the SAME two reasons as
   * planAwaitingBuild above — the templateId+firstBootCompletedAt early return
   * and the completedOnce latch fed by a clarify-only plan turn's 'completed'
   * task status (worker.js ~7090) — either of which would otherwise drop the
   * cover while the first build writes the app into an empty starter, leaving
   * the owner watching a blank preview. `hasCompletedTurn` itself is no longer
   * the problem here: CommandLog's `mode` field means the preceding plan turn
   * doesn't make it true early (2026-09-21).
   *
   * The caller (TaskDetailPage) now feeds this from `effectiveTurnMode`, not
   * the raw client-only `activeTurnMode` (2026-09-22, defect #4):
   * TaskChat rows persist the FINAL mode a command dispatched in
   * (server/models/TaskChat.js), and utils/runningTurnMode.ts reads it back
   * off the newest still-running command row. That closes the three gaps
   * activeTurnMode alone had — a Dashboard-dispatched first turn (never set
   * it to begin with), a page reload mid-build-after-plan (client state is
   * gone), and a stale WS 'pending' event racing the dispatch (used to wipe
   * it early) — so this clause now stays true across all three instead of
   * silently dropping the cover.
   */
  firstBuildAfterPlan?: boolean;
  /**
   * Output of completionUnproven: the task's latest turn ended 'completed' but
   * the server could not confirm the owner's app exists, and its check is
   * pending or decided "no". Overrides the 'completed' status and the
   * completion latch — and nothing else (ISSUE-005).
   */
  completionUnproven?: boolean;
}

/**
 * ISSUE-005 (QA 2026-10-04, D1 = A). A turn that ended 'completed' but proves
 * nothing — its stream was cut and the turn salvaged, or its file count failed
 * — is not the end of scaffolding: the server says hasCompletedTurn:false and
 * checks the live preview (server/lib/previewPresentability.js). The cover
 * holds only on a KNOWN answer: the check is still pending (the server ends
 * that after PENDING_MAX_MS) or it decided the page is not the owner's app.
 * When the check could not tell ('undecided') this is false and the cover
 * drops exactly as it always did, so a working app is never hidden by an
 * unknown. Every field must be the server's own, explicit word: a missing
 * field (older server, a socket event without facts) is never "unproven".
 */
export function completionUnproven(
  t: { status?: unknown; hasCompletedTurn?: unknown; appExistsVia?: unknown; presentability?: unknown } | null | undefined,
): boolean {
  if (!t) return false;
  return (
    t.status === 'completed' &&
    t.hasCompletedTurn === false &&
    !t.appExistsVia &&
    (t.presentability === 'pending' || t.presentability === 'decided')
  );
}

/**
 * THE single source of truth for "the scaffolding cover is up".
 * All clauses AND together: the cover shows only while every one holds.
 */
export function shouldShowScaffoldCover(i: ScaffoldCoverInputs): boolean {
  // A TEMPLATE that has already booted is never scaffolding again.
  //
  // A template import sets source='scratch' (verified on a live project), so the
  // source clause below does NOT exclude templates — and a template's first user
  // message is also its task's first turn, because the import runs no agent
  // turn and creates exactly one task. So the first follow-up message replayed
  // the first-run cover over a finished, running app, and because the cover
  // hides the preview controls, the customer had no way past it.
  //
  // Gated on firstBootCompletedAt rather than templateId alone, because the cover
  // IS correct for a template before its first boot: the app genuinely is not up.
  // And on templateId rather than firstBootCompletedAt alone, because a SCRATCH
  // build boots the vanilla starter — setting firstBootCompletedAt — before the
  // agent writes anything, and dropping the cover there would show the customer
  // the starter template, which is the whole reason the cover exists.
  //
  // The distinction is real: a scratch project is BUILT BY its first agent turn;
  // a template project is already built when it boots.
  // A saved-but-unbuilt plan covers the starter regardless of the clauses
  // below — see planAwaitingBuild. Guarded on first-task-and-never-completed so
  // it can only ever cover an untouched starter, never an app the owner has
  // actually built (a second plan on a live app must NOT hide their work).
  // Guarded on hasCompletedTurn, and deliberately NOT on completedOnce /
  // filesChanged:
  //   - hasCompletedTurn is now a trustworthy "the app exists" (2026-09-22).
  //     It is server-computed from CommandLog and since #680 excludes both
  //     `mode: 'plan'` rows and zero-file build turns (routes/tasks.js), so it
  //     can no longer go true off a plan turn — which was the ONLY reason this
  //     clause dropped the guard when it landed (2026-09-21). Re-arming it is
  //     what stops the cover latching over a finished app: see the regression
  //     below.
  //   - completedOnce means "a turn finished" by task.status, and a plan turn
  //     that only asks a clarifying question ("Does this match what you had in
  //     mind?") finishes too — the worker still publishes status 'completed'
  //     for that case (worker.js ~7090). completedOnce latches true before the
  //     real plan card ever arrived, so the cover never mounted (2026-09-21).
  //     That fix does not reach it, so it stays out of this clause.
  //   - filesChanged is a retired heuristic (see previewCoverScaffold-wiring's
  //     header, Talha 2026-09-07): a live turn flips it and drops the cover onto
  //     the untouched starter. It is doubly wrong here, because plan mode can
  //     still write files through the Bash gap, so it is non-zero during
  //     planning on exactly the flows this clause exists for.
  //
  // THE REGRESSION THIS GUARD FIXES (staging, 2026-09-22, customer report).
  // Without `!hasCompletedTurn` this clause fires on any 'approved' (saved)
  // plan card on a first task, whatever else has happened since — so a build
  // that ran off that plan and FINISHED left the owner staring at the
  // "Ready to build" cover over their working app. Two ways the card is still
  // 'approved' after a real build, both reachable today: the owner built by
  // typing in the composer rather than pressing the card's "Build this" (only
  // that button POSTs mark-plan-built), and the approval-gate timeout
  // auto-saves an unanswered plan as 'approved' (worker.js, isTimeout).
  // There was no way out either: the cover hides the preview controls, and
  // the cover's self-heal poll (TaskDetailPage) only refreshes
  // hasCompletedTurn — which this clause was ignoring — and bails once the
  // task is terminal. hasCompletedTurn is the one fact that means "the
  // preview is no longer the untouched starter", which is the whole contract
  // of this cover.
  //
  // isFirstTaskOnProject is durable, survives a reload, and cannot flip
  // mid-turn. The cover clears the moment the owner builds or discards, so the
  // narrow built-then-replanned case self-resolves rather than trapping anyone.
  if (i.planAwaitingBuild && i.isFirstTaskOnProject && !i.hasCompletedTurn) return true;

  // Same guards, plus never for a template: a template's app is genuinely up,
  // so watching it change during the build is the intended experience and a
  // cover would hide their own work.
  // No filesChangedCount guard here on purpose: the whole point is to hold the
  // cover up WHILE the first build writes files, so gating on "nothing written
  // yet" would drop it the moment the agent starts working.
  //
  // `!hasCompletedTurn` for the same reason as the clause above, and it is the
  // second half of the same customer report (staging, 2026-09-22): with the
  // app already built and live, every follow-up message the owner sent put the
  // cover BACK up over it — "Scaffolding · Getting your workspace ready" on a
  // finished app, because a build-mode turn on a first task was reason enough.
  // The owner typed "Where is the app please?" and the answer was hidden
  // behind the cover their question raised. A build that has already completed
  // with changed files means this is not the FIRST build any more, and a
  // scratch app that exists must be watchable while it changes — exactly the
  // reasoning the templateId clause above already encodes for templates.
  if (i.firstBuildAfterPlan && i.isFirstTaskOnProject && !i.templateId && !i.hasCompletedTurn) {
    return true;
  }

  if (i.templateId && i.firstBootCompletedAt) return false;

  return !!(
    i.source === 'scratch' &&
    i.devServerChecked &&
    i.isFirstTaskOnProject &&
    !i.hasCompletedTurn &&
    (i.completionUnproven || (i.status !== 'completed' && !i.completedOnce))
  );
}

/**
 * The server's settlement facts carried on a terminal task_status_changed
 * event (server/queue/worker.js, ISSUE-005), checked field by field. Only
 * fields present with a value the server can send are returned, so an event
 * without them (an older worker, another publish path) changes nothing.
 */
export function pickSettlementFacts(data: unknown): {
  hasCompletedTurn?: boolean;
  appExistsVia?: 'completed' | 'presentable' | null;
  interruptedOutcome?: string | null;
  presentability?: 'pending' | 'decided' | 'undecided' | null;
} {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const out: ReturnType<typeof pickSettlementFacts> = {};
  if (typeof d.hasCompletedTurn === 'boolean') out.hasCompletedTurn = d.hasCompletedTurn;
  if (d.appExistsVia === null || d.appExistsVia === 'completed' || d.appExistsVia === 'presentable') {
    out.appExistsVia = d.appExistsVia;
  }
  if (d.interruptedOutcome === null || typeof d.interruptedOutcome === 'string') {
    out.interruptedOutcome = d.interruptedOutcome as string | null;
  }
  if (d.presentability === null || d.presentability === 'pending' || d.presentability === 'decided' || d.presentability === 'undecided') {
    out.presentability = d.presentability;
  }
  return out;
}

/**
 * The completion latch's next value (TaskDetailPage `scaffoldCompleted`). A
 * completed turn latches it; a completion the server says proves nothing
 * (completionUnproven) never latches and clears a latch set before the
 * server's facts arrived — otherwise the gate's silent repair turn that
 * follows would run with the starter on screen (ISSUE-005).
 */
export function nextCompletionLatch(
  prev: boolean,
  t: Record<string, unknown> | null | undefined,
  executionStatus?: string | null,
): boolean {
  if (!t) return prev;
  if (completionUnproven(t)) return false;
  if (taskHasCompletedOnce({ status: t.status as string | null | undefined, executionStatus })) return true;
  return prev;
}
